import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ChatbotWidget from '../components/ChatbotWidget';
import DemoRequestsView from '../components/dashboard/views/DemoRequestsView';
import EveryorgSetupView from '../components/dashboard/views/EveryorgSetupView';

const SuperAdminDashboard = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // User management state
  const [allUsers, setAllUsers] = useState([]);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilters, setUserFilters] = useState({
    business_id: '',
    role: 'all',
    status: 'all',
    search: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch users when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, userFilters]);

  const fetchData = async () => {
    try {
      const [userRes, statsRes, businessesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/businesses`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);
      setBusinesses(businessesRes.data.businesses);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };
  
  // Fetch all platform users
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (userFilters.business_id) params.append('business_id', userFilters.business_id);
      if (userFilters.role !== 'all') params.append('role', userFilters.role);
      if (userFilters.status !== 'all') params.append('status', userFilters.status);
      if (userFilters.search) params.append('search', userFilters.search);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users?${params.toString()}`,
        { withCredentials: true }
      );
      
      setAllUsers(response.data.users || []);
      setUserBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };
  
  // Toggle user status (activate/deactivate)
  const handleToggleUserStatus = async (targetUser) => {
    setActionLoading(true);
    try {
      const newStatus = targetUser.is_active === false ? true : false;
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${targetUser.user_id}/status`,
        { is_active: !newStatus ? true : false },
        { withCredentials: true }
      );
      
      // Refresh users list
      fetchUsers();
      alert(`User ${!newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Reset user password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
      alert('Password reset successfully. User must log in again.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Change user role
  const handleChangeRole = async (targetUser, newRole) => {
    setActionLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${targetUser.user_id}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      
      fetchUsers();
      alert(`User role changed to ${newRole}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to change role');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Get role badge color
  const getRoleBadge = (role) => {
    const colors = {
      'super_admin': 'bg-purple-100 text-purple-800',
      'business_admin': 'bg-blue-100 text-blue-800',
      'view_only_admin': 'bg-gray-100 text-gray-800',
      'employee': 'bg-green-100 text-green-800'
    };
    const labels = {
      'super_admin': 'Super Admin',
      'business_admin': 'Business Admin',
      'view_only_admin': 'View Only',
      'employee': 'Employee'
    };
    return { color: colors[role] || 'bg-gray-100 text-gray-800', label: labels[role] || role };
  };

  // Search businesses
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/businesses/search?q=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.businesses || []);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to client-side filter if API doesn't exist
      const filtered = businesses.filter(b => 
        b.name?.toLowerCase().includes(query.toLowerCase()) ||
        b.business_id?.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(filtered);
    } finally {
      setIsSearching(false);
    }
  };

  // Manage business as admin
  const handleManageBusiness = (business) => {
    // Store the business context for super admin impersonation
    localStorage.setItem('superAdminManaging', JSON.stringify({
      business_id: business.business_id,
      business_name: business.name
    }));
    navigate(`/dashboard?manage=${business.business_id}`);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleViewDetails = async (business) => {
    setSelectedBusiness(business);
    setShowDetailsModal(true);
    try {
      // Fetch additional business details
      const [businessRes, campaignsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/businesses/${business.business_id}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/campaigns?business_id=${business.business_id}`, { withCredentials: true })
      ]);
      setBusinessDetails({
        ...businessRes.data,
        campaigns: campaignsRes.data || []
      });
    } catch (error) {
      console.error('Error fetching business details:', error);
      setBusinessDetails(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading platform data...</p>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Total Users</h3>
            <span className="text-3xl">👥</span>
          </div>
          <p className="text-4xl font-bold mb-1">{stats?.total_users || 0}</p>
          <div className="text-sm opacity-75">All platform users</div>
        </div>

        <div className="bg-gradient-to-br from-blue-400 to-blue-500 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Businesses</h3>
            <span className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">B</span>
          </div>
          <p className="text-4xl font-bold mb-1">{stats?.total_businesses || 0}</p>
          <div className="text-sm opacity-75">Active companies</div>
        </div>

        <div className="bg-gradient-to-br from-gray-600 to-gray-700 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Campaigns</h3>
            <span className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">C</span>
          </div>
          <p className="text-4xl font-bold mb-1">{stats?.total_campaigns || 0}</p>
          <div className="text-sm opacity-75">Total campaigns</div>
        </div>

        <div className="bg-gradient-to-br from-slate-500 to-slate-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Total Raised</h3>
            <span className="text-3xl">💰</span>
          </div>
          <p className="text-4xl font-bold mb-1">${(stats?.total_raised || 0).toLocaleString()}</p>
          <div className="text-sm opacity-75">Platform-wide</div>
        </div>

        <div className="bg-gradient-to-br from-blue-300 to-blue-400 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Donations</h3>
            <span className="text-3xl">🤝</span>
          </div>
          <p className="text-4xl font-bold mb-1">{stats?.total_donations || 0}</p>
          <div className="text-sm opacity-75">Total count</div>
        </div>
      </div>

      {/* Revenue Card */}
      {stats?.revenue && (
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-6 rounded-2xl shadow-lg">
          <h3 className="text-lg font-bold mb-4">Platform Revenue</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm opacity-80">Total Revenue</p>
              <p className="text-3xl font-bold">${(stats.revenue.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Donor Tips</p>
              <p className="text-2xl font-bold">${(stats.revenue.tips || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Matching Fees (2%)</p>
              <p className="text-2xl font-bold">${(stats.revenue.matching_fees || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Transactions</p>
              <p className="text-2xl font-bold">{stats.revenue.transactions_completed || 0}</p>
              <p className="text-xs opacity-60">{stats.revenue.transactions_pending || 0} pending</p>
            </div>
          </div>
        </div>
      )}

      {/* Businesses List */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h3 className="text-2xl font-bold text-gray-900">All Businesses</h3>
          
          {/* Search Bar */}
          <div className="relative w-full md:w-96">
            <input
              type="text"
              placeholder="Search businesses by name..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {isSearching && (
              <div className="absolute right-3 top-2.5">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* Display search results or all businesses */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(searchQuery.length >= 2 ? searchResults : businesses).map((business) => (
            <div key={business._id} className="border border-gray-200 rounded-xl p-6 hover:border-blue-500 transition-all hover:shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-lg text-gray-900">{business.name || 'Unnamed Business'}</h4>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                  Active
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Users:</span>
                  <span className="font-semibold">{business.user_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Campaigns:</span>
                  <span className="font-semibold">{business.campaign_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Raised:</span>
                  <span className="font-semibold text-green-600">${business.total_raised.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Donations:</span>
                  <span className="font-semibold">{business.donation_count}</span>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button 
                  onClick={() => handleViewDetails(business)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg font-medium transition-colors"
                >
                  View Details
                </button>
                <button 
                  onClick={() => handleManageBusiness(business)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors"
                >
                  🔧 Manage
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No results message */}
        {searchQuery.length >= 2 && searchResults.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No businesses found matching "{searchQuery}"
          </div>
        )}

        {businesses.length === 0 && searchQuery.length < 2 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">No businesses registered yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">All Platform Users</h3>
            <p className="text-sm text-gray-600">Manage users across all businesses</p>
          </div>
          <div className="text-sm text-gray-500">
            Total: <span className="font-semibold text-gray-900">{allUsers.length}</span> users
          </div>
        </div>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name, email, or ID..."
              value={userFilters.search}
              onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Business Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business</label>
            <select
              value={userFilters.business_id}
              onChange={(e) => setUserFilters(prev => ({ ...prev, business_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Businesses</option>
              {userBusinesses.map(b => (
                <option key={b.business_id} value={b.business_id}>{b.name}</option>
              ))}
            </select>
          </div>
          
          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={userFilters.role}
              onChange={(e) => setUserFilters(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="business_admin">Business Admin</option>
              <option value="view_only_admin">View Only</option>
              <option value="employee">Employee</option>
            </select>
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={userFilters.status}
              onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        
        {/* Users Table */}
        {usersLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-500">Loading users...</p>
          </div>
        ) : allUsers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No users found matching your criteria
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Business</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Employee ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Created</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((u) => {
                  const roleBadge = getRoleBadge(u.role);
                  const isActive = u.is_active !== false;
                  const isCurrentUser = u.user_id === user?.user_id;
                  
                  return (
                    <tr key={u.user_id} className={`border-b border-gray-100 hover:bg-gray-50 ${!isActive ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                            {u.name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{u.name || 'No Name'}</p>
                            <p className="text-sm text-gray-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-700">{u.business_name || '-'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadge.color}`}>
                          {roleBadge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-600">{u.employee_id || '-'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          {/* View/Edit */}
                          <button
                            onClick={() => { setSelectedUser(u); setShowUserModal(true); }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          
                          {/* Change Password */}
                          <button
                            onClick={() => { setSelectedUser(u); setShowPasswordModal(true); }}
                            className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Reset Password"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                          </button>
                          
                          {/* Activate/Deactivate */}
                          {!isCurrentUser && u.role !== 'super_admin' && (
                            <button
                              onClick={() => handleToggleUserStatus(u)}
                              disabled={actionLoading}
                              className={`p-2 rounded-lg transition-colors ${
                                isActive 
                                  ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                                  : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                              }`}
                              title={isActive ? 'Deactivate User' : 'Activate User'}
                            >
                              {isActive ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg m-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold text-gray-900">User Details</h3>
                <button
                  onClick={() => { setShowUserModal(false); setSelectedUser(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* User Avatar & Name */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
                  {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{selectedUser.name || 'No Name'}</h4>
                  <p className="text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">User ID:</span>
                  <p className="font-medium text-gray-900">{selectedUser.user_id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Employee ID:</span>
                  <p className="font-medium text-gray-900">{selectedUser.employee_id || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Business:</span>
                  <p className="font-medium text-gray-900">{selectedUser.business_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Department:</span>
                  <p className="font-medium text-gray-900">{selectedUser.department || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Job Title:</span>
                  <p className="font-medium text-gray-900">{selectedUser.job_title || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Location:</span>
                  <p className="font-medium text-gray-900">{selectedUser.location || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className={`font-medium ${selectedUser.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedUser.is_active !== false ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Auth Provider:</span>
                  <p className="font-medium text-gray-900">{selectedUser.auth_provider || 'email'}</p>
                </div>
              </div>
              
              {/* Role Changer */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Change Role</label>
                <div className="flex gap-2">
                  <select
                    defaultValue={selectedUser.role}
                    onChange={(e) => handleChangeRole(selectedUser, e.target.value)}
                    disabled={actionLoading || selectedUser.user_id === user?.user_id}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="business_admin">Business Admin</option>
                    <option value="view_only_admin">View Only</option>
                    <option value="employee">Employee</option>
                  </select>
                </div>
                {selectedUser.user_id === user?.user_id && (
                  <p className="text-xs text-gray-500 mt-1">You cannot change your own role</p>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => { setShowUserModal(false); setSelectedUser(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
              <p className="text-sm text-gray-500 mt-1">For: {selectedUser.email}</p>
            </div>
            
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-2">
                The user will be logged out and must use this new password to log in.
              </p>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowPasswordModal(false); setNewPassword(''); setSelectedUser(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={actionLoading || newPassword.length < 6}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {actionLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="Altrunic Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#0A58E5' }}>Altrunic</h1>
                <p className="text-sm text-gray-500">Super Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/crm')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                📊 CRM
              </button>
              <div className="hidden md:flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-lg">
                <span className="text-sm font-semibold text-blue-800">Super Admin</span>
              </div>
              <div className="hidden md:flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg">
                <span className="text-sm text-gray-600">Welcome,</span>
                <span className="text-sm font-semibold text-gray-900">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="admin-overview-tab"
            >
              Platform Overview
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="admin-users-tab"
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('demos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'demos'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="admin-demos-tab"
            >
              Demo Requests
            </button>
            <button
              onClick={() => setActiveTab('everyorg')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'everyorg'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="admin-everyorg-tab"
            >
              Every.org Rail
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'demos' && <DemoRequestsView />}
        {activeTab === 'everyorg' && <EveryorgSetupView />}
      </main>

      {/* Business Details Modal */}
      {showDetailsModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{selectedBusiness.name || 'Business Details'}</h2>
                  <p className="text-blue-100 mt-1">Business ID: {selectedBusiness.business_id}</p>
                </div>
                <button
                  onClick={() => { setShowDetailsModal(false); setBusinessDetails(null); }}
                  className="text-white hover:text-blue-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{selectedBusiness.user_count || 0}</p>
                  <p className="text-sm text-gray-600">Users</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedBusiness.campaign_count || 0}</p>
                  <p className="text-sm text-gray-600">Campaigns</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">${(selectedBusiness.total_raised || 0).toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Raised</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{selectedBusiness.donation_count || 0}</p>
                  <p className="text-sm text-gray-600">Donations</p>
                </div>
              </div>

              {/* Business Info */}
              {businessDetails && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Business Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Industry:</span>
                      <span className="ml-2 font-medium">{businessDetails.industry || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <span className="ml-2 font-medium">{businessDetails.size || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <span className="ml-2 font-medium">
                        {businessDetails.city && businessDetails.state 
                          ? `${businessDetails.city}, ${businessDetails.state}` 
                          : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Website:</span>
                      <span className="ml-2 font-medium">{businessDetails.website || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Campaigns List */}
              {businessDetails?.campaigns && businessDetails.campaigns.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Campaigns ({businessDetails.campaigns.length})</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {businessDetails.campaigns.map((campaign, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{campaign.title}</p>
                          <p className="text-sm text-gray-500">{campaign.charity_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">${(campaign.raised_amount || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-400">of ${(campaign.goal_amount || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {businessDetails?.campaigns?.length === 0 && (
                <p className="text-gray-500 text-center py-4">No campaigns created yet</p>
              )}

              {!businessDetails && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading details...</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => { setShowDetailsModal(false); setBusinessDetails(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ChatbotWidget context="support" />
    </div>
  );
};

export default SuperAdminDashboard;
